import { Fragment, memo, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Tooltip from 'components/tooltip/Tooltip'
import Button from 'components/form/Button'
import { isEmpty } from 'utils'
import { useDialog } from 'features/dialog/Dialog'
import { TalkChannelRegisterPopup } from 'features/talkChannelRegister/TalkChannelRegisterPopup'
import { Link, useParams, useRouteMatch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  BizChannelTypeNames,
  BizChannelTypes,
  ReviewStatus,
  ReviewTargetType,
} from 'features/Codes'
import ChannelOperationStatus from 'features/channel/list/ChannelOperationStatus'
import WebsiteTalkChannelItem from './WebsiteTalkChannelItem'
import TalkChannelItem from './TalkChannelItem'
import { showReviewReason } from '../../reviewReason/reviewReasonSlice'

const { REACT_APP_PF_HOST } = process.env

const ChannelListItem = memo(
  ({
    id,
    bizChannelType,
    name,
    urls,
    reviewStatus,
    isPaused,
    pausedReason,
    talkChannelAccounts,
    isDisabled,
    talkChannel,
    categoryFullPath,
  }) => {
    const dispatch = useDispatch()
    const { accountId: adAccountId } = useParams()
    const { isLoading } = useSelector(state => state.reviewReason)
    const { url: routeURL } = useRouteMatch()
    const [
      TalkChannelRegister,
      showTalkChannelRegister,
      hideTalkChannelRegister,
    ] = useDialog()
    const [
      NoChannelsAvailable,
      showNoChannelsAvailable,
      hideNoChannelsAvailable,
    ] = useDialog()

    const handleReasonClick = useCallback(
      () =>
        dispatch(
          showReviewReason({
            id,
            reviewTargetType: ReviewTargetType.BIZCHANNEL,
          })
        ),
      [dispatch, id]
    )

    return (
      <>
        <TalkChannelRegister type="basic_layer" className="bizch_layer">
          <TalkChannelRegisterPopup
            bizChannelId={id}
            handleClose={hideTalkChannelRegister}
            handleShowNoChannelsAvailable={showNoChannelsAvailable}
          />
        </TalkChannelRegister>

        <NoChannelsAvailable type="basic_layer2" title="카카오톡 채널 등록">
          <strong className="tit_layer">카카오톡 채널 등록</strong>
          <p className="txt_layer">연동 가능한 카카오톡 채널이 없습니다.</p>
          <div className="btn_group">
            <div className="align_c">
              <a
                href={REACT_APP_PF_HOST}
                target="_blank"
                rel="noopener noreferrer"
                className="btn_gm gm_bl"
                onClick={hideNoChannelsAvailable}>
                <span className="inner_g">카카오톡 채널 만들기</span>
              </a>
            </div>
          </div>
        </NoChannelsAvailable>

        <td>
          <div className="inner_tbl">
            <Link to={`${routeURL}/${id}`}>{name}</Link>
            <br />
            {urls.map((url, i) => (
              <Fragment key={i}>
                <span className="link_txt">
                  <span className="limit_line">{url}</span>
                </span>
                <br />
              </Fragment>
            ))}
          </div>
        </td>
        <td>
          <div className="inner_tbl">{BizChannelTypeNames[bizChannelType]}</div>
        </td>
        <td className="td_state">
          <div className="inner_tbl">
            {(ReviewStatus[reviewStatus] === ReviewStatus.REJECTED_APPROVAL && (
              <>
                <Tooltip
                  position="top alignRight"
                  offsetX={20}
                  content={
                    <>
                      소재가 심사 가이드에 맞지 않아 보류되었습니다.
                      <br />
                      보류 사유 확인 후 비즈채널정보를 수정하세요.
                    </>
                  }>
                  {ReviewStatus.REJECTED_APPROVAL.title}
                </Tooltip>
                <br />
                <div className="wrap_btn">
                  <Button
                    size="small"
                    onClick={handleReasonClick}
                    loading={isLoading}>
                    사유
                  </Button>
                </div>
              </>
            )) ||
              ReviewStatus[reviewStatus].title}
          </div>
        </td>
        <td>
          <div className="inner_tbl">
            <ChannelOperationStatus
              isPaused={isPaused}
              pausedReason={pausedReason}
              isDisabled={isDisabled}
              reviewStatus={ReviewStatus[reviewStatus]}
            />
          </div>
        </td>
        <td
          className={classNames('td_talk', {
            align_c:
              bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL &&
              isEmpty(talkChannelAccounts),
          })}>
          <div className="inner_tbl">
            {bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL && (
              <>
                {isEmpty(talkChannelAccounts) ? (
                  <div className="wrap_btn">
                    <Button size="small" onClick={showTalkChannelRegister}>
                      채널 등록
                    </Button>
                  </div>
                ) : (
                  talkChannelAccounts.map(talkChannelAccount => (
                    <WebsiteTalkChannelItem
                      key={talkChannelAccount.uuid}
                      bizChannelId={id}
                      talkChannelId={talkChannelAccount.id}
                      talkChannelName={talkChannelAccount.name}
                      talkChannelStatus={talkChannelAccount.status}
                    />
                  ))
                )}
              </>
            )}
            {bizChannelType === BizChannelTypes.TALK_CHANNEL && (
              <TalkChannelItem {...talkChannel} />
            )}
          </div>
        </td>
        <td>
          <div className="inner_tbl">
            <span className="limit_line">{categoryFullPath}</span>
          </div>
        </td>
        <td className="align_c">
          <div className="inner_tbl">
            <div className="wrap_btn">
              <Button
                size="small"
                linkTo={`/${adAccountId}/documents/add/${id}`}>
                심사서류 등록
              </Button>
            </div>
          </div>
        </td>
      </>
    )
  }
)

export default ChannelListItem
const talkChannelPropTypes = PropTypes.shape({
  name: PropTypes.string,
  uuid: PropTypes.string,
  enable: PropTypes.bool,
})
ChannelListItem.displayName = 'ChannelListItem'
ChannelListItem.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  urls: PropTypes.arrayOf(PropTypes.string),
  reviewStatus: PropTypes.string,
  pausedReason: PropTypes.string,
  talkChannel: talkChannelPropTypes,
  talkChannelAccounts: PropTypes.arrayOf(talkChannelPropTypes),
  isPaused: PropTypes.bool,
  isDisabled: PropTypes.bool,
  categoryFullPath: PropTypes.string,
  bizChannelType: PropTypes.oneOf([
    BizChannelTypes.WEBSITE_BIZ_CHANNEL,
    BizChannelTypes.TALK_CHANNEL,
  ]),
}
